import { useMemo } from "react";
import SEO from "@/components/feature/SEO";
import AdminLayout from "@/components/feature/AdminLayout";
import { useAdminData } from "@/hooks/useAdmin";
import LeadSourceAnalytics from "./components/LeadSourceAnalytics";
import TodaysAppointmentsWidget from "./components/TodaysAppointments";
import {
  Users,
  UserCheck,
  CalendarDays,
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  Phone,
  Mail,
  Clock,
  MapPin,
  CalendarCheck,
} from "lucide-react";

export default function AdminDashboard() {
  const { stats, leads, appointments, loading, error, updateAppointmentStatus, realDataLoaded, realtimeConnected } = useAdminData();

  const recentLeads = useMemo(() => leads.slice(0, 5), [leads]);
  const upcomingAppointments = useMemo(
    () => appointments.filter((a) => ["scheduled", "confirmed"].includes(a.status)).slice(0, 5),
    [appointments]
  );

  const statCards = [
    {
      label: "Total Leads",
      value: stats.totalLeads,
      icon: Users,
      change: "+12%",
      positive: true,
      color: "bg-blue-50 text-blue-600",
    },
    {
      label: "New Leads",
      value: stats.newLeads,
      icon: UserCheck,
      change: "+5%",
      positive: true,
      color: "bg-emerald-50 text-emerald-600",
    },
    {
      label: "Converted",
      value: stats.convertedLeads,
      icon: TrendingUp,
      change: "+8%",
      positive: true,
      color: "bg-amber-50 text-amber-600",
    },
    {
      label: "Conversion Rate",
      value: `${stats.conversionRate}%`,
      icon: ArrowUpRight,
      change: "+2%",
      positive: true,
      color: "bg-purple-50 text-purple-600",
    },
    {
      label: "Appointments",
      value: stats.totalAppointments,
      icon: CalendarDays,
      change: "-3%",
      positive: false,
      color: "bg-rose-50 text-rose-600",
    },
    {
      label: "Completed",
      value: stats.completedAppointments,
      icon: Clock,
      change: "+15%",
      positive: true,
      color: "bg-cyan-50 text-cyan-600",
    },
  ];

  const statusStyles: Record<string, string> = {
    new: "bg-amber-100 text-amber-700",
    contacted: "bg-blue-100 text-blue-700",
    qualified: "bg-purple-100 text-purple-700",
    converted: "bg-emerald-100 text-emerald-700",
    lost: "bg-red-100 text-red-700",
    scheduled: "bg-amber-100 text-amber-700",
    confirmed: "bg-blue-100 text-blue-700",
    completed: "bg-emerald-100 text-emerald-700",
    cancelled: "bg-red-100 text-red-700",
    no_show: "bg-slate-100 text-slate-600",
  };

  return (
    <AdminLayout>
      <SEO title="Admin Dashboard | ExpertAdvise.in" noindex />
      <div className="p-4 md:p-8 max-w-7xl mx-auto">
        <div className="mb-6 md:mb-8 flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-slate-900">Dashboard</h1>
            <p className="text-sm text-slate-500 mt-1">Overview of your leads, appointments, and conversion metrics.</p>
          </div>
          {!realDataLoaded && (
            <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
              Demo Data — Real submissions will appear here
            </span>
          )}
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
            Failed to load data: {error}
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-6 md:mb-8">
          {statCards.map((card, i) => (
            <div
              key={i}
              className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between mb-2">
                <div className={`w-8 h-8 rounded-lg ${card.color} flex items-center justify-center`}>
                  <card.icon className="w-4 h-4" />
                </div>
                <span
                  className={`flex items-center gap-0.5 text-[10px] font-semibold ${
                    card.positive ? "text-emerald-600" : "text-red-500"
                  }`}
                >
                  {card.positive ? (
                    <ArrowUpRight className="w-3 h-3" />
                  ) : (
                    <ArrowDownRight className="w-3 h-3" />
                  )}
                  {card.change}
                </span>
              </div>
              <div className="text-xl md:text-2xl font-bold text-slate-900">{card.value}</div>
              <div className="text-xs text-slate-500 mt-0.5">{card.label}</div>
            </div>
          ))}
        </div>
        {loading && (
          <div className="text-center py-4 text-sm text-slate-400">Loading data...</div>
        )}

        {/* Empty state when no real data */}
        {leads.length === 0 && appointments.length === 0 && !loading && (
          <div className="bg-white rounded-xl border border-slate-200 p-8 md:p-12 text-center mb-6">
            <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Users className="w-7 h-7 text-slate-400" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">No Data Yet</h3>
            <p className="text-sm text-slate-500 max-w-md mx-auto mb-4">
              No leads or appointments have been submitted yet. When someone fills out a form on your website, it will appear here automatically.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-slate-400">
              <span className="flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full ${realtimeConnected ? "bg-emerald-500" : "bg-slate-300"}`} />
                Realtime {realtimeConnected ? "connected" : "disconnected"}
              </span>
              <span>|</span>
              <span>Check /contact and /book-appointment forms</span>
            </div>
          </div>
        )}

        {/* Analytics + Today's Appointments */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
          <LeadSourceAnalytics leads={leads} />
          <TodaysAppointmentsWidget appointments={appointments} onStatusChange={updateAppointmentStatus} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {/* Recent Leads */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 md:p-5 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-base font-bold text-slate-900">Recent Leads</h2>
              <a
                href="/admin/leads"
                className="text-xs font-medium text-primary-600 hover:text-primary-700"
              >
                View All
              </a>
            </div>
            <div className="divide-y divide-slate-100">
              {recentLeads.map((lead) => (
                <div key={lead.id} className="p-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm text-slate-900 truncate">
                          {lead.name}
                        </span>
                        <span
                          className={`text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize ${
                            statusStyles[lead.status] || "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {lead.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {lead.phone}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {lead.city}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {lead.insurance_type} · {lead.source}
                        {lead.assigned_to && (
                          <span className="ml-1 text-teal-600">· Assigned: {lead.assigned_to}</span>
                        )}
                      </div>
                    </div>
                    <div className="text-[10px] text-slate-400 shrink-0 whitespace-nowrap">
                      {new Date(lead.created_at).toLocaleDateString("en-IN")}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Upcoming Appointments */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 md:p-5 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-base font-bold text-slate-900">Upcoming Appointments</h2>
              <a
                href="/admin/appointments"
                className="text-xs font-medium text-primary-600 hover:text-primary-700"
              >
                View All
              </a>
            </div>
            <div className="divide-y divide-slate-100">
              {upcomingAppointments.map((apt) => (
                <div key={apt.id} className="p-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm text-slate-900 truncate">
                          {apt.name}
                        </span>
                        <span
                          className={`text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize ${
                            statusStyles[apt.status] || "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {apt.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <CalendarCheck className="w-3 h-3" />
                          {apt.preferred_date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {apt.preferred_time}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {apt.insurance_type} · {apt.city}{apt.source ? ` · ${apt.source}` : ""}
                      </div>
                    </div>
                    <div className="text-[10px] text-slate-400 shrink-0 whitespace-nowrap">
                      {apt.preferred_date}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}